// Deliverables register (V24-V25, V30-V32, E09/E10): one row per deliverable, inline edits, Excel round trip.
import { useMutation, useQuery } from "convex/react";
import { useMemo, useState } from "react";
import { Plus, Trash2, Undo2 } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import { BD_LABEL, useProject } from "@/hooks/project";
import { Badge, Button, Checkbox, Field, Input, Modal, PageHeader, Select, cn, useRun, useToast, fmtNum } from "@/components/ui";
import { DataGrid, type Col } from "@/components/DataGrid";
import { ExportButton, ImportButton } from "@/components/Importer";
import { exportDeliverables, parseDeliverables, resolveDeliverables, type ExistingDeliverable } from "@/lib/formats";
import { deliverableStatus } from "@/engine/compute";
import { currentWeight } from "@/engine/series";
import { LOE_CHAIN, type Breakdown } from "@/engine/types";
import { validateDeliverable } from "@/engine/validate";
import { sortedPeriods, toEngineDeliverable } from "@/shared/model";
import type { ResolvedRow } from "@/lib/formats";
import type { ProjectCtx } from "@/hooks/project";

/** Run the same rules the server enforces, so the preview shows them before anything is sent. */
function checkImportRules(rows: ResolvedRow[], P: ProjectCtx): ResolvedRow[] {
  const periods = sortedPeriods(P.periods);
  const lastClosed = P.periods.filter((p) => p.closed).at(-1);
  const existing = new Map(P.b.deliverables.map((d) => [d.code, d]));
  const BLOCK = ["skippedStep", "earnedBackwards", "beforeCalendar", "afterCalendar", "badWeight", "unknownChain", "chainMismatch", "unknownCode"];
  return rows.map((r0) => {
    let r = r0;
    if (!r.row || r.status === "unchanged" || r.status === "error") return r;
    const errs: string[] = [];
    const ed = toEngineDeliverable({ ...r.row!, _id: "x" }, P.chains, []);
    if (ed) for (const i of validateDeliverable(ed, { periods, knownCodes: P.known })) if (i.severity === "error" && BLOCK.includes(i.code)) errs.push(i.message);
    const ex = existing.get(r.code);
    // Warn (don't block) on earned dates after the current cutoff (Cesar's test, 30:46).
    const cur = P.periods[P.currentIdx];
    const future = cur ? r.row.steps.filter((s) => s.earned && s.earned > cur.cutoff).map((s) => `⚠ ${s.milestone} earned ${s.earned}, after the current cutoff ${cur.cutoff}`) : [];
    if (future.length) r = { ...r, changes: [...future, ...r.changes] };
    if (P.project.baselineLocked) {
      if (!ex) errs.push("The baseline is locked; add new deliverables one at a time with a change order (Add deliverable)");
      else {
        if (ex.weight !== r.row!.weight) errs.push("The baseline is locked; change the weight with a change order");
        r.row!.steps.forEach((s, i) => (ex.steps[i]?.planned ?? null) !== (s.planned ?? null) && errs.push(`The baseline is locked; planned date of ${s.milestone} can't change`));
      }
    }
    if (lastClosed)
      r.row!.steps.forEach((s, i) => {
        const old = ex?.steps[i]?.earned ?? null;
        const now = s.earned ?? null;
        if (old !== now && ((old && old <= lastClosed.cutoff) || (now && now <= lastClosed.cutoff))) errs.push(`${s.milestone}: earned date is in a closed period (through ${lastClosed.cutoff})`);
      });
    return errs.length ? { ...r, status: "error" as const, errors: [...new Set(errs)] } : r;
  });
}

type D = Doc<"deliverables">;

export function toExport(d: D): ExistingDeliverable {
  return {
    _id: d._id,
    code: d.code,
    title: d.title,
    category: d.category,
    clientRef: d.clientRef,
    free: d.free,
    docType: d.docType,
    codes: d.codes,
    weight: d.weight,
    weightManuf: d.weightManuf,
    unit: d.unit,
    chainCode: d.chainCode,
    steps: d.steps.map((s) => ({ milestone: s.milestone, planned: s.planned ?? null, forecast: s.forecast ?? null, earned: s.earned ?? null, codes: s.codes ?? {}, planningRef: s.planningRef })),
  };
}

export function DeliverablesPage() {
  const P = useProject();
  const { project, b, prep, currentIdx, canEdit, nodes, activeBreakdowns, issues, chains } = P;
  const update = useMutation(api.deliverables.update);
  const importRows = useMutation(api.deliverables.importRows);
  const remove = useMutation(api.deliverables.remove);
  const restore = useMutation(api.deliverables.restore);
  const run = useRun();
  const toast = useToast();
  const [sel, setSel] = useState<D[]>([]);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [q, setQ] = useState("");
  const status = useMemo(() => new Map(deliverableStatus(prep, Math.max(0, currentIdx)).map((s) => [s.id, s])), [prep, currentIdx]);
  const rows = useMemo(
    () => b.deliverables.filter((d) => !q || `${d.code} ${d.title ?? ""} ${d.chainCode} ${Object.values(d.codes).join(" ")}`.toLowerCase().includes(q.toLowerCase())).sort((a, c) => a.code.localeCompare(c.code, undefined, { numeric: true })),
    [b.deliverables, q],
  );
  const patch = (d: D, p: Parameters<typeof update>[0]["patch"]) => run(() => update({ id: d._id, patch: p }));
  const current = useMemo(() => new Map(P.data.deliverables.map((d) => [d.id, currentWeight(d)])), [P.data]);
  const codeCols: Col<D>[] = activeBreakdowns
    .filter((t) => !(t === "obs" && project.options.singleCenter))
    .filter((t) => nodes[t].length > 0 || b.deliverables.some((d) => d.codes[t]))
    .map((t) => ({
      id: t,
      title: BD_LABEL[t],
      width: 90,
      options: nodes[t].map((n) => n.code),
      get: (r) => r.codes[t],
      set: canEdit ? (r, v) => void patch(r, { codes: { ...r.codes, [t]: v === null ? undefined : String(v) } }) : undefined,
    }));
  const cols: Col<D>[] = [
    { id: "code", title: "DLV reference", width: 250, get: (r) => r.code, tone: (r) => (issues.get(r._id)?.some((i) => i.severity === "error") ? "error" : undefined) },
    { id: "title", title: "Title", width: 240, get: (r) => r.title, set: canEdit ? (r, v) => void patch(r, { title: String(v ?? "") }) : undefined },
    { id: "category", title: "Category", width: 80, get: (r) => r.category, set: canEdit ? (r, v) => void patch(r, { category: v === null ? undefined : String(v) }) : undefined },
    { id: "docType", title: "Doc type", width: 80, get: (r) => r.docType, set: canEdit ? (r, v) => void patch(r, { docType: v === null ? undefined : String(v) }) : undefined },
    ...codeCols,
    {
      id: "weight",
      title: "Weight",
      width: 80,
      kind: "number",
      digits: 1,
      get: (r) => current.get(r._id) ?? r.weight,
      set: canEdit ? (r, v) => void patch(r, { weight: Number(v ?? 0) }) : undefined,
      // After an approved change order the weight only moves through change orders.
      readonly: (r) => project.baselineLocked || (current.get(r._id) ?? r.weight) !== r.weight,
    },
    { id: "unit", title: "Unit", width: 50, options: ["h", "$"], get: (r) => r.unit ?? "h", set: canEdit ? (r, v) => void patch(r, { unit: v as "h" }) : undefined, readonly: () => project.baselineLocked },
    { id: "chain", title: "Chain", width: 80, options: [...chains.keys()], get: (r) => r.chainCode, set: canEdit ? (r, v) => void patch(r, { chainCode: String(v ?? "") }) : undefined, readonly: (r) => project.baselineLocked || !!r.loe || r.steps.some((s) => s.earned) },
    // Level of effort (Cesar, Oct 15): its dates instead of milestones.
    ...(b.deliverables.some((d) => d.loe)
      ? ([
          { id: "loeStart", title: "LOE start", width: 90, kind: "date", get: (r) => r.loe?.start, set: canEdit ? (r, v) => r.loe && v && void patch(r, { loe: { ...r.loe, start: String(v) } }) : undefined, readonly: (r) => !r.loe || project.baselineLocked, tone: (r) => (r.loe ? undefined : "muted") },
          { id: "loeEnd", title: "LOE end", width: 90, kind: "date", get: (r) => r.loe?.end, set: canEdit ? (r, v) => r.loe && v && void patch(r, { loe: { ...r.loe, end: String(v) } }) : undefined, readonly: (r) => !r.loe || project.baselineLocked, tone: (r) => (r.loe ? undefined : "muted") },
        ] as Col<D>[])
      : []),
    { id: "pe", title: "Earned %", width: 80, kind: "pct", get: (r) => status.get(r._id)?.pctEarned ?? null },
    { id: "pp", title: "Planned %", width: 80, kind: "pct", get: (r) => status.get(r._id)?.pctPlanned ?? null },
    { id: "pf", title: "Forecast %", width: 80, kind: "pct", get: (r) => status.get(r._id)?.pctForecast ?? null },
    { id: "last", title: "Last earned", width: 90, get: (r) => status.get(r._id)?.lastEarned },
    { id: "next", title: "Next", width: 80, get: (r) => status.get(r._id)?.nextMilestone },
    {
      id: "issues",
      title: "Issues",
      width: 260,
      get: (r) => issues.get(r._id)?.map((i) => i.message).join("; "),
      tone: (r) => (issues.get(r._id)?.some((i) => i.severity === "error") ? "error" : issues.get(r._id)?.length ? "warn" : undefined),
    },
  ];
  const totals = useMemo(() => {
    const w = (list: D[], unit: "h" | "$") => list.filter((d) => (d.unit ?? "h") === unit).reduce((s, d) => s + (current.get(d._id) ?? 0), 0);
    return { h: w(b.deliverables, "h"), usd: w(b.deliverables, "$"), shownH: w(rows, "h"), shownUsd: w(rows, "$") };
  }, [b.deliverables, rows, current]);
  // The total follows the filter (Cesar, Oct 16): "4 of 66 deliverables · 1,234 h of 9,456 h".
  const filtered = rows.length !== b.deliverables.length;

  return (
    <div className="space-y-3">
      <PageHeader
        title="Deliverables"
        sub={
          <>
            {filtered ? (
              <>
                <b>{rows.length}</b> of {b.deliverables.length} deliverables · <b>{fmtNum(totals.shownH)} h</b> of {fmtNum(totals.h)} h
                {totals.usd ? ` · $${fmtNum(totals.shownUsd)} of $${fmtNum(totals.usd)} (kept separate)` : ""}
              </>
            ) : (
              <>
                {b.deliverables.length} deliverables · {fmtNum(totals.h)} h{totals.usd ? ` · $${fmtNum(totals.usd)} (kept separate)` : ""}
              </>
            )}
            {project.baselineLocked && <Badge tone="amber" className="ml-2">baseline locked: weights change through change orders</Badge>}
          </>
        }
        actions={
          <>
            <ExportButton file={`${project.code}-LISTDLV.xlsx`} sheets={() => [exportDeliverables(project.code, b.deliverables.map(toExport))]} />
            {canEdit && (
              <ImportButton
                build={(sheets) => {
                  const { items, errors } = parseDeliverables(sheets[0].rows);
                  const res = checkImportRules(resolveDeliverables(items, b.deliverables.map(toExport), chains), P);
                  // X07: rows that change or clear an earned date need a reason.
                  const revs = res.filter((r) => r.status === "changed").flatMap((r) => r.changes.filter((c) => / earned: [^(]/.test(c)).map((c) => `${r.code} ${c}`));
                  const why = { v: "" };
                  return {
                    problems: errors.filter((e) => !/no DLV REFERENCE/.test(e.message)).map((e) => `Row ${e.line}: ${e.message}`),
                    note: (
                      <p className="text-sm text-slate-600">
                        Columns that aren't in the file keep their current values. Weights and cumulative % always come from the chain, not the file. Rules checked on save: no skipped steps, no backwards earned
                        dates, dates inside the calendar, codes in the breakdowns, closed periods and the baseline lock.
                        {revs.length > 0 && (
                          <span className="mt-2 block rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
                            {revs.length} earned date{revs.length > 1 ? "s" : ""} would change or be cleared ({revs.slice(0, 3).join("; ")}
                            {revs.length > 3 ? "; ..." : ""}). Give a reason for the audit log:
                            <Input aria-label="Import reason" className="mt-1" onChange={(e) => (why.v = e.target.value)} />
                          </span>
                        )}
                      </p>
                    ),
                    rows: res.map((r) => ({ line: r.line, key: r.code || "(blank)", status: r.status, detail: r.errors.length ? r.errors.join("\n") : r.changes.slice(0, 6).join("\n") + (r.changes.length > 6 ? `\n+${r.changes.length - 6} more` : "") })),
                    commit: async () => {
                      if (revs.length && !why.v.trim()) throw new Error("Enter a reason for the earned-date changes");
                      return importRows({
                        projectId: project._id,
                        reason: why.v.trim() || undefined,
                        rows: res.filter((r) => r.row && (r.status === "new" || r.status === "changed")).map((r) => {
                          const x = r.row!;
                          return {
                            code: x.code,
                            title: x.title || undefined,
                            category: x.category || undefined,
                            clientRef: x.clientRef || undefined,
                            docType: x.docType || undefined,
                            free: x.free,
                            codes: x.codes,
                            weight: x.weight,
                            weightManuf: x.weightManuf ?? undefined,
                            unit: x.unit,
                            chainCode: x.chainCode,
                            steps: x.steps.map((s) => ({ milestone: s.milestone, planned: s.planned ?? null, forecast: s.forecast ?? null, earned: s.earned ?? null, codes: s.codes ?? {}, ...(s.planningRef ? { planningRef: s.planningRef } : {}) })),
                          };
                        }),
                      });
                    },
                  };
                }}
              />
            )}
            {canEdit && (
              <Button
                variant="danger"
                disabled={!sel.length}
                onClick={async () => {
                  const ids = sel.map((d) => d._id);
                  const ok = await run(() => remove({ ids }));
                  if (ok !== undefined) toast({ text: `Deleted ${ids.length}`, tone: "info", action: { label: "Undo", run: () => void run(() => restore({ ids })) } });
                }}
              >
                <Trash2 className="size-4" /> Delete {sel.length || ""}
              </Button>
            )}
            {canEdit && (
              <Button onClick={() => setBulk(true)} disabled={!chains.size || project.baselineLocked} title={project.baselineLocked ? "Baseline locked: new deliverables come in one at a time with a change order" : "Pick a standard list of deliverables from the reference library"}>
                <Plus className="size-4" /> Add from reference library
              </Button>
            )}
            {canEdit && (
              <Button variant="primary" onClick={() => setAdding(true)} disabled={!chains.size} title={project.baselineLocked ? "Baseline locked: new deliverables come in with a change order" : undefined}>
                <Plus className="size-4" /> Add deliverable
              </Button>
            )}
          </>
        }
      />
      <div className="flex items-center gap-3">
        <Input placeholder="Filter by reference, title, chain or code" value={q} onChange={(e) => setQ(e.target.value)} className="w-80" aria-label="Filter deliverables" />
        {b.deletedDeliverables.length > 0 && (
          <button className="text-sm text-slate-500 underline" onClick={() => setShowDeleted(true)}>
            {b.deletedDeliverables.length} deleted
          </button>
        )}
        {!chains.size && <span className="text-sm text-amber-700">Load chains in the library before adding deliverables.</span>}
      </div>
      <DataGrid rows={rows} cols={cols} height="calc(100vh - 240px)" freeze={1} onSelectRows={setSel} onEdit={(edits) => edits.forEach((e) => e.col.set?.(e.row, e.value))} onError={(m) => toast({ text: m, tone: "error" })} />
      {adding && <AddDeliverable onClose={() => setAdding(false)} />}
      {bulk && <AddFromRefs onClose={() => setBulk(false)} />}
      <Modal open={showDeleted} onClose={() => setShowDeleted(false)} title="Deleted deliverables">
        <ul className="space-y-1">
          {b.deletedDeliverables.map((d) => (
            <li key={d._id} className="flex items-center justify-between text-sm">
              <span>
                {d.code} <span className="text-slate-500">{d.title}</span>
              </span>
              <Button size="sm" onClick={() => run(() => restore({ ids: [d._id as Id<"deliverables">] }), `Restored ${d.code}`)}>
                <Undo2 className="size-3.5" /> Restore
              </Button>
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
}

function AddDeliverable({ onClose }: { onClose: () => void }) {
  const { project, chains, nodes, activeBreakdowns, b } = useProject();
  const lib = useQuery(api.library.list, { projectId: project._id });
  const refList = useQuery(api.library.refs, {});
  const create = useMutation(api.deliverables.create);
  const run = useRun();
  const [f, setF] = useState({ code: "", title: "", docType: "", engCode: "", category: "", weight: "", unit: "h" as "h" | "$", chainCode: "", codes: {} as Partial<Record<Breakdown, string>>, co: "", coDesc: "", loe: false, loeStart: "", loeEnd: "" });
  const locked = project.baselineLocked;
  // Say what's missing instead of silently disabling Add (Cesar's test, 13:17).
  const missing = [
    !f.code.trim() && "DLV reference",
    !f.loe && !f.chainCode && "chain",
    f.loe && !f.loeStart && "LOE start",
    f.loe && !f.loeEnd && "LOE end",
    (f.weight === "" || !(Number(f.weight) >= 0)) && "weight",
    locked && !f.co.trim() && "change order number",
  ].filter(Boolean) as string[];
  // A picked project library lists only the picked chains (plus any in use).
  const chainList = lib ? lib.chains : [...chains.values()];
  const refs = refList ?? [];
  const matches = refs.filter((r) => r.docType === f.docType && (r.engCode ?? "") === f.engCode);
  const match = matches[0];
  // V07: the reference library defaults the chain, weight and title by document type and engineering code.
  const applyRef = (docType: string, engCode: string) => {
    const all = refs.filter((x) => x.docType === docType && (x.engCode ?? "") === engCode);
    const r = all.length === 1 ? all[0] : undefined;
    setF((cur) => ({
      ...cur,
      docType,
      engCode,
      ...(r ? { title: cur.title || r.title, chainCode: cur.chainCode || (r.chainCode ?? ""), weight: cur.weight || (r.defaultWeight !== undefined ? String(r.defaultWeight) : ""), category: cur.category || (r.category ?? "") } : {}),
    }));
  };
  const suggest = () => {
    const pattern = project.codification || "{PROJECT}-{WBS}-{DOCTYPE}-{ENGCODE}-{SERIAL}";
    const code = codeFromPattern(pattern, { project: project.code, wbs: f.codes.wbs, dbs: f.codes.dbs, docType: f.docType, engCode: f.engCode }, new Set(b.deliverables.map((d) => d.code)));
    setF((c) => ({ ...c, code }));
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Add deliverable"
      wide
      footer={
        <>
          {missing.length > 0 && <span className="mr-auto self-center text-xs text-slate-500">Still needed: {missing.join(", ")}</span>}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={missing.length > 0}
            onClick={async () => {
              const id = await run(
                () =>
                  create({
                    projectId: project._id,
                    code: f.code,
                    title: f.title || undefined,
                    docType: f.docType || undefined,
                    category: f.category || undefined,
                    codes: Object.fromEntries(Object.entries(f.codes).filter(([, v]) => v)),
                    weight: Number(f.weight),
                    unit: f.unit,
                    chainCode: f.loe ? LOE_CHAIN : f.chainCode,
                    ...(f.loe ? { loe: { start: f.loeStart, end: f.loeEnd } } : {}),
                    ...(locked ? { changeOrder: { number: f.co.trim(), description: f.coDesc.trim() || undefined } } : {}),
                  }),
                `Added ${f.code}`,
              );
              if (id) onClose();
            }}
          >
            Add
          </Button>
        </>
      }
    >
      {locked && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          The baseline is locked, so this deliverable is new scope. It's added with 0 hours plus a pending change order for its weight; it counts from the period the change order is approved.
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Field label="Change order number *">
              <Input value={f.co} onChange={(e) => setF({ ...f, co: e.target.value })} placeholder="e.g. CO-004" />
            </Field>
            <Field label="Change order description">
              <Input value={f.coDesc} onChange={(e) => setF({ ...f, coDesc: e.target.value })} placeholder="e.g. adds datasheets for pump P-101 (ref. original deliverable)" />
            </Field>
          </div>
        </div>
      )}
      <div className="mb-3 flex gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="radio" name="dlv-kind" className="accent-brand-600" checked={!f.loe} onChange={() => setF({ ...f, loe: false })} /> Milestone chain
        </label>
        <label className="flex items-center gap-1.5" title="Work with no milestones, such as meetings and reviews. It earns the budget planned for each elapsed period, like an indirect, but it is part of the discipline's scope and budget.">
          <input type="radio" name="dlv-kind" className="accent-brand-600" checked={f.loe} onChange={() => setF({ ...f, loe: true })} /> Level of effort (LOE)
        </label>
      </div>
      {f.loe && (
        <p className="mb-3 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
          Level of effort has no milestones. Its weight is spread evenly over the periods between its start and end, and each period earns what was planned for it once the period is reached. It counts in the discipline's budget, the curves and the reports.
        </p>
      )}
      <div className="grid grid-cols-3 gap-3">
        <Field label="Doc type">
          <Select value={f.docType} onChange={(e) => applyRef(e.target.value, f.engCode)}>
            <option value="" />
            {[...new Set([...(lib?.docTypes ?? []).map((d) => d.code), ...refs.map((r) => r.docType)])].sort().map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
        </Field>
        <Field label="Eng. code" hint="Optional. With the doc type, looks up the reference library to fill the chain and weight.">
          <Input value={f.engCode} onChange={(e) => applyRef(f.docType, e.target.value)} />
        </Field>
        <Field label="Category" hint="Optional free label, e.g. [D] document, [P] procurement.">
          <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        </Field>
        {matches.length === 1 && <p className="col-span-3 -mt-1 text-xs text-emerald-700">From the reference library: {match.title} · chain {match.chainCode ?? "none"}{match.defaultWeight !== undefined ? ` · ${match.defaultWeight} h` : ""}</p>}
        {matches.length > 1 && (
          <Field label={`${matches.length} reference entries match; pick one`} className="col-span-3">
            <Select
              value=""
              onChange={(e) => {
                const r = matches[Number(e.target.value)];
                if (r) setF((cur) => ({ ...cur, title: r.title, chainCode: r.chainCode ?? "", weight: r.defaultWeight !== undefined ? String(r.defaultWeight) : cur.weight, category: r.category ?? cur.category }));
              }}
            >
              <option value="">Choose...</option>
              {matches.map((r, i) => (
                <option key={r._id} value={i}>
                  {r.title} · chain {r.chainCode ?? "none"}
                  {r.procChainCode ? ` + ${r.procChainCode}` : ""}
                  {r.defaultWeight !== undefined ? ` · ${r.defaultWeight} h` : ""}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {activeBreakdowns.map((t) => (
          <Field key={t} label={BD_LABEL[t]}>
            <Select value={f.codes[t] ?? ""} onChange={(e) => setF({ ...f, codes: { ...f.codes, [t]: e.target.value } })}>
              <option value="" />
              {nodes[t].map((n) => (
                <option key={n.code} value={n.code}>
                  {n.code} · {n.title}
                </option>
              ))}
            </Select>
          </Field>
        ))}
        <Field label="DLV reference *" hint="The deliverable's unique number, e.g. the document number." className="col-span-2">
          <div className="flex gap-2">
            <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
            <Button type="button" onClick={suggest} title="Build from the codification pattern">
              Suggest
            </Button>
          </div>
        </Field>
        <Field label="Title">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </Field>
        {f.loe ? (
          <>
            <Field label="LOE start *">
              <Input type="date" value={f.loeStart} onChange={(e) => setF({ ...f, loeStart: e.target.value })} />
            </Field>
            <Field label="LOE end *">
              <Input type="date" value={f.loeEnd} onChange={(e) => setF({ ...f, loeEnd: e.target.value })} />
            </Field>
          </>
        ) : (
          <Field label="Chain *">
            <Select value={f.chainCode} onChange={(e) => setF({ ...f, chainCode: e.target.value })}>
              <option value="" />
              {chainList.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} · {c.steps.map((s) => s.milestone).join("/")}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={locked ? "Weight (hours the change order adds) *" : "Weight (hours) *"}>
          <Input type="number" min={0} value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} />
        </Field>
        <Field label="Unit" hint={<>Hours and dollars are never added together</>}>
          <Select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value as "h" })}>
            <option value="h">hours</option>
            <option value="$">$ (manufacturing)</option>
          </Select>
        </Field>
      </div>
    </Modal>
  );
}

/** DLV reference from the project's codification pattern; {SERIAL} takes the next number not in `used`. */
function codeFromPattern(pattern: string, parts: { project: string; wbs?: string; dbs?: string; docType?: string; engCode?: string }, used: Set<string>): string {
  const base = pattern
    .replace("{PROJECT}", parts.project)
    .replace("{WBS}", parts.wbs ?? "")
    .replace("{DBS}", parts.dbs ?? "")
    .replace("{DOCTYPE}", parts.docType ?? "")
    .replace("{ENGCODE}", parts.engCode ?? "");
  if (!base.includes("{SERIAL}")) return base;
  for (let n = 1; n < 100000; n++) {
    const code = base.replace("{SERIAL}", String(n).padStart(4, "0"));
    if (!used.has(code)) return code;
  }
  return base;
}

type DraftRow = { key: string; code: string; title: string; docType: string; category?: string; chainCode: string; weight: string; codes: Partial<Record<Breakdown, string>> };

/**
 * Add a standard set of deliverables from the reference library in one go (Cesar, Oct 16: a standard deliverable
 * list for engineering, procurement, construction and commissioning). Pick entries, set the codes they share,
 * review the generated references and weights, then add them all at once.
 */
function AddFromRefs({ onClose }: { onClose: () => void }) {
  const { project, chains, nodes, activeBreakdowns, b } = useProject();
  const refList = useQuery(api.library.refs, {});
  const importRows = useMutation(api.deliverables.importRows);
  const run = useRun();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [disc, setDisc] = useState("");
  const [docType, setDocType] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [codes, setCodes] = useState<Partial<Record<Breakdown, string>>>({});
  const [discAsDbs, setDiscAsDbs] = useState(true);
  const [draft, setDraft] = useState<DraftRow[] | null>(null);
  const bds = activeBreakdowns.filter((x) => nodes[x].length > 0);
  const plural = (n: number) => `${n} deliverable${n === 1 ? "" : "s"}`;
  const refs = useMemo(() => refList ?? [], [refList]);
  const dbsCodes = useMemo(() => new Set(nodes.dbs.map((n) => n.code)), [nodes.dbs]);
  const uniq = (xs: (string | undefined)[]) => [...new Set(xs.filter((x): x is string => !!x))].sort((a, c) => a.localeCompare(c, undefined, { numeric: true }));
  const cats = uniq(refs.map((r) => r.category));
  const discs = uniq(refs.map((r) => r.discipline));
  const types = uniq(refs.map((r) => r.docType));
  const shown = refs.filter(
    (r) =>
      (!q || `${r.docType} ${r.engCode ?? ""} ${r.title} ${r.chainCode ?? ""}`.toLowerCase().includes(q.toLowerCase())) &&
      (!cat || r.category === cat) &&
      (!disc || r.discipline === disc) &&
      (!docType || r.docType === docType),
  );
  const LIMIT = 400;
  const toggle = (id: string, on: boolean) =>
    setSel((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const review = () => {
    const used = new Set(b.deliverables.map((d) => d.code));
    const pattern = project.codification || "{PROJECT}-{WBS}-{DOCTYPE}-{ENGCODE}-{SERIAL}";
    const rows = refs
      .filter((r) => sel.has(r._id))
      .map((r) => {
        const c = { ...codes };
        if (discAsDbs && r.discipline && dbsCodes.has(r.discipline)) c.dbs = r.discipline;
        const code = codeFromPattern(pattern, { project: project.code, wbs: c.wbs, dbs: c.dbs, docType: r.docType, engCode: r.engCode }, used);
        used.add(code);
        return { key: r._id, code, title: r.title, docType: r.docType, category: r.category, chainCode: r.chainCode ?? "", weight: r.defaultWeight !== undefined ? String(r.defaultWeight) : "", codes: c };
      });
    setDraft(rows);
  };

  const problems = (d: DraftRow, all: DraftRow[]) =>
    [
      !d.code.trim() && "no reference",
      d.code.trim() && (b.deliverables.some((x) => x.code === d.code.trim()) || all.filter((x) => x.code.trim() === d.code.trim()).length > 1) && "reference already used",
      !chains.has(d.chainCode) && (d.chainCode ? `chain ${d.chainCode} isn't in the library` : "no chain"),
      (d.weight === "" || !(Number(d.weight) >= 0)) && "no weight",
    ].filter(Boolean) as string[];

  if (draft) {
    const bad = draft.filter((d) => problems(d, draft).length);
    const setRow = (i: number, p: Partial<DraftRow>) => setDraft(draft.map((x, j) => (j === i ? { ...x, ...p } : x)));
    const total = draft.reduce((s, d) => s + (Number(d.weight) || 0), 0);
    return (
      <Modal
        open
        onClose={onClose}
        title={`Add ${plural(draft.length)} from the reference library`}
        wide="xl"
        footer={
          <>
            <span className="mr-auto self-center text-xs text-slate-500">
              {fmtNum(total)} h in total{bad.length ? ` · ${bad.length} rows need fixing (or remove them)` : ""}
            </span>
            <Button onClick={() => setDraft(null)}>Back</Button>
            <Button
              variant="primary"
              disabled={!draft.length || bad.length > 0}
              onClick={async () => {
                const r = await run(
                  () =>
                    importRows({
                      projectId: project._id,
                      rows: draft.map((d) => ({
                        code: d.code.trim(),
                        title: d.title || undefined,
                        docType: d.docType || undefined,
                        category: d.category || undefined,
                        codes: Object.fromEntries(Object.entries(d.codes).filter(([, v]) => v)),
                        weight: Number(d.weight),
                        unit: "h" as const,
                        chainCode: d.chainCode,
                        steps: chains.get(d.chainCode)!.steps.map((s) => ({ milestone: s.milestone, planned: null, forecast: null, earned: null, codes: {} })),
                      })),
                    }),
                  `Added ${plural(draft.length)}`,
                );
                if (r) onClose();
              }}
            >
              Add {plural(draft.length)}
            </Button>
          </>
        }
      >
        <p className="mb-2 text-sm text-slate-600">References come from the project's codification pattern ({project.codification || "{PROJECT}-{WBS}-{DOCTYPE}-{ENGCODE}-{SERIAL}"}). Edit any cell before adding. Planned dates are entered afterwards in Progress update or by Excel import.</p>
        <div className="max-h-[60vh] overflow-auto rounded border border-slate-200">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-2 py-1.5">DLV reference</th>
                <th className="px-2 py-1.5">Title</th>
                <th className="px-2 py-1.5">Doc type</th>
                {bds.map((t) => (
                  <th key={t} className="px-2 py-1.5">
                    {BD_LABEL[t]}
                  </th>
                ))}
                <th className="px-2 py-1.5">Chain</th>
                <th className="px-2 py-1.5 text-right">Weight (h)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {draft.map((d, i) => {
                const p = problems(d, draft);
                return (
                  <tr key={d.key} className={cn("border-t border-slate-100 align-top", p.length && "bg-red-50")}>
                    <td className="px-1 py-1">
                      <Input aria-label={`Reference ${i + 1}`} className="h-8 w-72" value={d.code} onChange={(e) => setRow(i, { code: e.target.value })} />
                      {p.length > 0 && <div className="mt-0.5 text-xs text-red-700">{p.join(", ")}</div>}
                    </td>
                    <td className="px-1 py-1">
                      <Input aria-label={`Title ${i + 1}`} className="h-8 w-64" value={d.title} onChange={(e) => setRow(i, { title: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5">{d.docType}</td>
                    {bds.map((t) => (
                      <td key={t} className="px-1 py-1">
                        <Select aria-label={`${BD_LABEL[t]} ${i + 1}`} className="h-8 w-24" value={d.codes[t] ?? ""} onChange={(e) => setRow(i, { codes: { ...d.codes, [t]: e.target.value || undefined } })}>
                          <option value="" />
                          {nodes[t].map((n) => (
                            <option key={n.code}>{n.code}</option>
                          ))}
                        </Select>
                      </td>
                    ))}
                    <td className="px-1 py-1">
                      <Select aria-label={`Chain ${i + 1}`} className="h-8 w-28" value={chains.has(d.chainCode) ? d.chainCode : ""} onChange={(e) => setRow(i, { chainCode: e.target.value })}>
                        <option value="">{d.chainCode && !chains.has(d.chainCode) ? `${d.chainCode}?` : ""}</option>
                        {[...chains.keys()].map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </Select>
                    </td>
                    <td className="px-1 py-1">
                      <Input aria-label={`Weight ${i + 1}`} type="number" min={0} className="h-8 w-24 text-right" value={d.weight} onChange={(e) => setRow(i, { weight: e.target.value })} />
                    </td>
                    <td className="px-1 py-1">
                      <Button size="sm" variant="ghost" aria-label={`Remove row ${i + 1}`} onClick={() => setDraft(draft.filter((_, j) => j !== i))}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add deliverables from the reference library"
      wide="xl"
      footer={
        <>
          <span className="mr-auto self-center text-xs text-slate-500">{sel.size} picked</span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!sel.size} onClick={review}>
            Review {sel.size || ""}
          </Button>
        </>
      }
    >
      {!refList ? (
        <p className="text-sm text-slate-500">Loading the reference library...</p>
      ) : !refs.length ? (
        <p className="text-sm text-slate-600">The reference library is empty. Add or import entries in Library &gt; Reference deliverables first.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Input placeholder="Search doc type, eng. code, title or chain" aria-label="Search reference deliverables" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" />
            <Select value={cat} onChange={(e) => setCat(e.target.value)} className="w-44" aria-label="Category">
              <option value="">All categories</option>
              {cats.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
            <Select value={disc} onChange={(e) => setDisc(e.target.value)} className="w-44" aria-label="Discipline">
              <option value="">All disciplines</option>
              {discs.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
            <Select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-40" aria-label="Doc type">
              <option value="">All doc types</option>
              {types.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs">
            <span className="text-slate-500">
              {shown.length} of {refs.length} entries{shown.length > LIMIT ? `; showing the first ${LIMIT}, filter to narrow` : ""}
            </span>
            <button type="button" className="text-brand-700 underline" onClick={() => setSel((s) => new Set([...s, ...shown.slice(0, LIMIT).map((r) => r._id)]))}>
              Tick all shown
            </button>
            <button type="button" className="text-slate-600 underline" onClick={() => setSel(new Set())}>
              Clear
            </button>
          </div>
          <div className="mt-2 max-h-[45vh] overflow-y-auto rounded border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="w-8 px-2 py-1.5" />
                  <th className="px-2 py-1.5">Doc type</th>
                  <th className="px-2 py-1.5">Eng. code</th>
                  <th className="px-2 py-1.5">Title</th>
                  <th className="px-2 py-1.5">Discipline</th>
                  <th className="px-2 py-1.5">Chain</th>
                  <th className="px-2 py-1.5 text-right">Weight</th>
                  <th className="px-2 py-1.5">Category</th>
                </tr>
              </thead>
              <tbody>
                {shown.slice(0, LIMIT).map((r) => (
                  <tr key={r._id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <input type="checkbox" className="size-4 accent-brand-600" aria-label={`Pick ${r.title}`} checked={sel.has(r._id)} onChange={(e) => toggle(r._id, e.target.checked)} />
                    </td>
                    <td className="px-2 py-1">{r.docType}</td>
                    <td className="px-2 py-1">{r.engCode}</td>
                    <td className="px-2 py-1">{r.title}</td>
                    <td className="px-2 py-1">{r.discipline}</td>
                    <td className={cn("px-2 py-1", r.chainCode && !chains.has(r.chainCode) && "text-amber-700")}>{r.chainCode}</td>
                    <td className="num px-2 py-1 text-right">{r.defaultWeight !== undefined ? fmtNum(r.defaultWeight) : ""}</td>
                    <td className="px-2 py-1">{r.category}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 rounded border border-slate-200 p-3">
            <p className="mb-2 text-sm font-medium">Codes for all the picked deliverables</p>
            <div className="grid grid-cols-4 gap-3">
              {bds.map((t) => (
                <Field key={t} label={BD_LABEL[t]}>
                  <Select value={codes[t] ?? ""} onChange={(e) => setCodes({ ...codes, [t]: e.target.value || undefined })}>
                    <option value="" />
                    {nodes[t].map((n) => (
                      <option key={n.code} value={n.code}>
                        {n.code} · {n.title}
                      </option>
                    ))}
                  </Select>
                </Field>
              ))}
            </div>
            <div className="mt-2">
              <Checkbox label="Use each entry's discipline as its DBS when that code is in the DBS breakdown" checked={discAsDbs} onChange={setDiscAsDbs} />
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
